import { MemberAvatar } from '@/components/MemberAvatar';
import { UtilizationBar } from '@/components/charts/UtilizationBar';
import { cn } from '@/lib/utils';

const fmtHours = (minutes: number): string => {
  if (!minutes || minutes <= 0) return '0h';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const fmtMoney = (cents: number | undefined | null, currency: string | undefined): string => {
  if (cents === undefined || cents === null) return '—';
  if (!currency) return (cents / 100).toFixed(2);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    return (cents / 100).toFixed(2);
  }
};

export interface ReportsUserRowProps {
  userId: string;
  name: string | null;
  email: string | null;
  totalMinutes: number;
  billableMinutes: number;
  amountCents: number;
  currency: string | undefined;
}

/**
 * Reports redesign — by-user row. CSS-grid layout matching the
 * prototype's `.v1-rep-user` template:
 *   `28px  minmax(180px,1fr)  1fr  90px  90px  110px`
 *    avatar  name+email          util   total billable amount
 *
 * The utilization bar uses the gradient fill from
 * `UtilizationBar` so each row has a quick-glance billable share.
 */
export const ReportsUserRow = ({
  userId,
  name,
  email,
  totalMinutes,
  billableMinutes,
  amountCents,
  currency,
}: ReportsUserRowProps) => (
  <div
    className={cn(
      'grid items-center gap-x-3.5 border-b px-[18px] py-3 transition-colors duration-100',
      'grid-cols-[28px_minmax(180px,1fr)_1fr_90px_90px_110px]',
      'border-[var(--glass-card-border)] last:border-b-0',
      'hover:bg-[oklch(0_0_0/0.025)] dark:hover:bg-[oklch(1_0_0/0.04)]',
    )}
  >
    <MemberAvatar
      id={userId}
      name={name}
      email={email}
      size="sm"
      className="!size-7 !text-[10px]"
    />

    <div className="flex min-w-0 flex-col gap-0.5">
      <span
        className={cn(
          'text-foreground truncate text-[13px] font-medium',
        )}
      >
        {name ?? '—'}
      </span>
      <span
        className={cn(
          'text-foreground-faint truncate font-mono text-[11px]',
        )}
      >
        {email ?? ''}
      </span>
    </div>

    <UtilizationBar totalMinutes={totalMinutes} billableMinutes={billableMinutes} />

    <span
      className={cn(
        'text-foreground-muted text-right font-mono text-[12px] tabular-nums',
      )}
    >
      {fmtHours(totalMinutes)}
    </span>
    <span
      className={cn(
        'text-foreground-muted text-right font-mono text-[12px] tabular-nums',
      )}
    >
      {fmtHours(billableMinutes)}
    </span>
    <span
      className={cn(
        'text-foreground text-right font-mono text-[13px] font-semibold tracking-[-0.01em] tabular-nums',
      )}
    >
      {fmtMoney(amountCents, currency)}
    </span>
  </div>
);

export const ReportsUserHeader = () => (
  <div
    className={cn(
      'grid gap-x-3.5 border-b px-[18px] py-2.5 font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em]',
      'grid-cols-[28px_minmax(180px,1fr)_1fr_90px_90px_110px]',
      'text-foreground-faint border-[var(--glass-card-border)]',
      'bg-[oklch(0_0_0/0.015)] dark:bg-[oklch(1_0_0/0.02)]',
    )}
  >
    <span />
    <span>Member</span>
    <span>Utilization</span>
    <span className="text-right">Total</span>
    <span className="text-right">Billable</span>
    <span className="text-right">Amount</span>
  </div>
);
